// ===========================================
// Teacher Revenue Chart Component
// ===========================================
// Bar chart showing revenue attribution by teacher

import { Card, CardContent, CardHeader, Typography, Box, Divider, CircularProgress } from '@mui/material';
import { AttachMoney as MoneyIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import { TeacherRevenueData } from '../../api/reports.api';
import { formatCurrency } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface TeacherRevenueChartProps {
  data: TeacherRevenueData[];
  loading?: boolean;
}

// ===========================================
// CUSTOM TOOLTIP
// ===========================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <Card sx={{ p: 1.5, minWidth: 220 }}>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Total Revenue:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {formatCurrency(data.totalRevenue)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Students:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.studentCount}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Revenue/Student:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {formatCurrency(data.revenuePerStudent)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Invoices:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.invoiceCount}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function TeacherRevenueChart({ data, loading }: TeacherRevenueChartProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon color="primary" />
              <Typography variant="h6">Revenue by Teacher</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon color="primary" />
              <Typography variant="h6">Revenue by Teacher</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No revenue data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Sort by revenue descending, take top 10
  const topTeachers = [...data]
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 10)
    .map((teacher) => ({
      name: teacher.teacherName.split(' ')[0], // First name only
      revenue: teacher.totalRevenue / 100, // Convert cents to dollars for display
      totalRevenue: teacher.totalRevenue, // Keep original for tooltip
      studentCount: teacher.studentCount,
      revenuePerStudent: teacher.revenuePerStudent,
      invoiceCount: teacher.invoiceCount,
    }));

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon color="primary" />
            <Typography variant="h6">Revenue by Teacher</Typography>
          </Box>
        }
        subheader={`Top ${topTeachers.length} teachers by revenue generated`}
      />
      <Divider />
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={topTeachers}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              tickFormatter={(value) => `$${(value / 1).toFixed(0)}`}
              label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="revenue"
              name="Revenue ($)"
              fill="#4580E4"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
